<script setup lang="ts">
import { toMoney } from "@chatfood/core-utils";
import { AtomText, AtomIcon, AtomButton } from "@chatfood/design-system";
import { LoyaltyNativeBalance } from "../loyalty-native-balance";
import { css } from "./css";
import { useCustomerStore } from "~/stores/customer";
import { useBusinessStore, useLoyaltyProgramStore } from "#imports";
import { useLocale } from "~/composables/use-locale";
import { useNavigation } from "~/composables/use-navigation";

const loyaltyProgramStore = useLoyaltyProgramStore();
const businessStore = useBusinessStore();
const { t } = useLocale();

const customerStore = useCustomerStore();

const { push } = useNavigation();

const toHomePage = () => {
  push("order", {});
};
</script>

<template>
  <div :class="css()">
    <div :class="css('details')" data-test="user-details">
      <LoyaltyNativeBalance />
      <AtomText color="color-text-secondary" type="h3">
        {{ customerStore.name }}
      </AtomText>
    </div>
    <div :class="css('content')">
      <AtomText
        :class="css('title')"
        type="body-bold"
        color="color-text-secondary"
      >
        {{ t("components.loyalty_program_authenticated.title") }}
      </AtomText>
      <div :class="css('items')">
        <AtomText :class="css('item')" data-test="item">
          <AtomIcon name="checkmark" size="medium" color="color-primary" />
          {{
            t("components.loyalty_program_authenticated.cashback_text", {
              amount: loyaltyProgramStore.orderAmountPercentageWorth,
            })
          }}
        </AtomText>
        <AtomText :class="css('item')" data-test="item">
          <AtomIcon name="checkmark" size="medium" color="color-primary" />
          {{
            t("components.loyalty_program_authenticated.redeem_text", {
              amount: loyaltyProgramStore.maximumRedeemedOrderPercentage,
            })
          }}
        </AtomText>
        <AtomText :class="css('item')" data-test="item">
          <AtomIcon name="checkmark" size="medium" color="color-primary" />
          {{
            t("components.loyalty_program_authenticated.discount_text", {
              amount: toMoney(
                loyaltyProgramStore.maximumRedeemedOrderAmount,
                businessStore.currency,
              ),
            })
          }}
        </AtomText>
      </div>
    </div>
    <div
      :class="[css('cta'), businessStore.isMobileApp && css('cta', 'fixed')]"
    >
      <AtomButton
        wide
        size="large"
        type="button"
        variant="primary"
        theme="ghost"
        data-test="cta-order"
        @click="toHomePage"
      >
        {{ t("components.loyalty_program_authenticated.order_cta") }}
      </AtomButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
.loyalty-program-authenticated {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: var(--spacing-3xl);

  @media (width <= $breakpoint-sm) {
    gap: var(--spacing-xl);
  }

  &__details {
    display: flex;
    gap: var(--spacing-md);
    align-items: center;
    justify-content: center;
    flex-direction: column;
  }

  &__content {
    display: flex;
    gap: var(--spacing-md);
    flex-direction: column;
  }

  &__title {
    display: block;
  }

  &__items {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-sm);
  }

  &__item {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: var(--spacing-md);
  }

  &__cta {
    width: 100%;
    position: sticky;
    bottom: 0;
    padding: var(--spacing-md);
    background: var(--color-background-shell);

    &--fixed {
      position: fixed;
    }

    @media (width >= $breakpoint-sm) {
      width: auto;
      position: initial;
      bottom: initial;
      padding-block: initial;
      background: initial;
    }
  }
}
</style>
